import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, AlertTriangle, Cog, UserCheck, FileCheck2, ClipboardCheck,
    Timer, Hourglass, GaugeCircle, Activity as ActivityIcon, Clock, Flag, Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { relativeTime } from "@/lib/constants";
import { firstNameFromLastFirst } from "@/lib/greeting";

const KPI_TILES = [
    { key: "clients_blocked_today", label: "Clients blocked today", icon: AlertTriangle, to: "/app/work-queue?stage=waiting_on_client", tone: "text-rose-700", requires: "work-queue" },
    { key: "ready_for_processing", label: "Ready for processing", icon: Cog, to: "/app/work-queue?stage=processing", requires: "work-queue" },
    { key: "waiting_on_client", label: "Waiting on client", icon: Hourglass, to: "/app/job-flow", tone: "text-amber-700", requires: "job-flow" },
    { key: "ready_for_review", label: "Ready for review", icon: UserCheck, to: "/app/work-queue?stage=review", requires: "work-queue" },
    { key: "ready_to_file", label: "Ready to file", icon: FileCheck2, to: "/app/work-queue?stage=ready_to_file", requires: "work-queue" },
    { key: "avg_days_waiting", label: "Avg days waiting", icon: Timer, to: "/app/job-flow", suffix: "d", requires: "job-flow" },
    { key: "queue_age_days", label: "Oldest queue age", icon: GaugeCircle, to: "/app/job-flow", suffix: "d", toneIf: (v) => v > 14 ? "text-rose-700" : "", requires: "job-flow" },
    { key: "breached", label: "SLA breached", icon: ClipboardCheck, to: "/app/work-queue?breached=1", tone: "text-rose-700", requires: "work-queue" },
];

const JOB_TYPE_LABEL = {
    income_tax: "Income Tax", gst: "GST", payroll: "Payroll", others: "Others",
};

export default function FirmDashboard() {
    const { tenant, user } = useAuth();
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";
    const [stats, setStats] = useState(null);
    const [overview, setOverview] = useState(null);
    const [layout, setLayout] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [team, setTeam] = useState([]);
    const [stuck, setStuck] = useState([]);
    const [clientsById, setClientsById] = useState({});

    useEffect(() => {
        api.get("/work-queue/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/tenant/overview").then((r) => setOverview(r.data)).catch(() => {});
        api.get("/nav/layout").then((r) => setLayout(r.data)).catch(() => {});
        api.get("/job-flow/jobs", { params: { scope: isAdmin ? "team" : "mine" } }).then((r) => setJobs(r.data || [])).catch(() => {});
        api.get("/job-flow/team").then((r) => setTeam(r.data || [])).catch(() => {});
        api.get("/clients").then((r) => {
            const m = {};
            for (const c of (r.data || [])) m[c.id] = c;
            setClientsById(m);
        }).catch(() => {});
        if (isAdmin) api.get("/job-flow/stuck").then((r) => setStuck(r.data || [])).catch(() => {});
    }, [isAdmin]);

    // Any item the admin has hidden via Settings → Navigation layout gets
    // dropped from the Dashboard too (both the KPI tile and anywhere else
    // we deep-link into it). "hidden" is a flat list of KNOWN item keys.
    const hiddenItems = useMemo(() => new Set(layout?.hidden || []), [layout]);
    const visibleItems = useMemo(() => {
        const s = new Set();
        for (const g of (layout?.groups || [])) {
            for (const k of (g.items || [])) s.add(k);
        }
        return s;
    }, [layout]);
    function tileVisible(t) {
        if (!layout) return true;                 // first paint
        if (hiddenItems.has(t.requires)) return false;
        if (visibleItems.size > 0 && !visibleItems.has(t.requires)) return false;
        return true;
    }

    const visibleTiles = [];

    return (
        <div>
            <PageHeader
                eyebrow={`${tenant?.name || "Firm"} · job flow`}
                title={`Kia ora, ${firstNameFromLastFirst(user?.name) || "team"}.`}
                description="One view of the firm's jobs — who's doing what, what's stuck, what's due."
                testId="firm-dashboard-header"
            />

            {visibleTiles.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5" data-testid="dashboard-kpis">
                    {visibleTiles.map((t) => {
                        const v = stats ? stats[t.key] : null;
                        const tone = t.tone || (t.toneIf ? t.toneIf(v) : "");
                        return (
                            <Link
                                key={t.key}
                                to={t.to}
                                className="rounded-lg border border-[#E4E4E7] bg-white p-3 hover:shadow-sm transition-shadow group block"
                                data-testid={`kpi-${t.key}`}
                            >
                                <div className="flex items-center justify-between">
                                    <t.icon className={`h-4 w-4 ${tone || "text-[#5E636E]"}`} />
                                    <ArrowRight className="h-3.5 w-3.5 text-[#5E636E] opacity-0 group-hover:opacity-100" />
                                </div>
                                <div className={`mt-2 font-display text-2xl tabular-nums leading-none ${tone}`} data-testid={`kpi-${t.key}-value`}>
                                    {stats ? (v ?? 0) : "—"}{t.suffix || ""}
                                </div>
                                <div className="mt-1.5 text-[11px] text-[#5E636E] leading-tight">{t.label}</div>
                            </Link>
                        );
                    })}
                </div>
            )}

            <div className="mt-6 grid gap-4 lg:grid-cols-3">
                {/* Job Flow — Team board (admin) / My queue (staff) */}
                <div className="lg:col-span-2 co-card co-card-pad" data-testid="dash-jobs-widget">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="co-label">Job Flow</div>
                            <h3 className="font-display text-xl font-medium mt-1">
                                {isAdmin ? "Who's working on what" : "My open jobs"}
                            </h3>
                        </div>
                        <Link to="/app/job-flow" className="text-[12px] text-[#5E636E] hover:text-[#0F1115] inline-flex items-center gap-1">
                            Open Job Flow <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>

                    {isAdmin ? (
                        <TeamSummary jobs={jobs} team={team} clientsById={clientsById} stuck={stuck} />
                    ) : (
                        <MyQueue jobs={jobs} clientsById={clientsById} />
                    )}
                </div>

                <div className="co-card co-card-pad">
                    <div className="co-label">Recent activity</div>
                    <h3 className="font-display text-xl font-medium mt-1">Latest events</h3>
                    <ul className="mt-4 divide-y divide-[#E4E4E7]" data-testid="recent-activity">
                        {!overview && <li className="py-3 text-sm text-[#5E636E]">Loading…</li>}
                        {overview && overview.recent_activity.length === 0 && (
                            <li className="py-6 text-sm text-[#5E636E]">Nothing yet.</li>
                        )}
                        {overview?.recent_activity?.slice(0, 8).map((a) => (
                            <li key={a.id} className="py-2 flex items-start gap-2">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#5E636E]" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12px] truncate">
                                        <span className="font-medium">{a.actor_name || "System"}</span>{" "}
                                        <span className="text-[#5E636E]">{a.action.replace(/_/g, " ")}</span>
                                    </div>
                                    <div className="text-[10px] text-[#5E636E]">{relativeTime(a.created_at)}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

function TeamSummary({ jobs, team, clientsById, stuck }) {
    const owners = (team || []).filter((u) => u.role === "firm_admin" || u.role === "firm_staff");
    const counts = useMemo(() => {
        const m = new Map();
        for (const u of owners) m.set(u.id, 0);
        for (const j of jobs) if (m.has(j.current_owner_user_id)) m.set(j.current_owner_user_id, m.get(j.current_owner_user_id) + 1);
        return m;
    }, [jobs, owners]);
    const stuckClip = stuck.slice(0, 4);
    return (
        <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {owners.map((u) => (
                    <Link to="/app/job-flow" key={u.id}
                        className="rounded-md border border-[#E4E4E7] bg-white p-2.5 hover:border-[#0F1115]"
                        data-testid={`dash-team-${u.id}`}>
                        <div className="flex items-center justify-between">
                            <div className="font-medium text-[13px] text-[#0F1115] truncate">{u.name || u.email}</div>
                            <span className="inline-flex items-center justify-center h-5 min-w-[22px] px-1 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-semibold">
                                {counts.get(u.id) || 0}
                            </span>
                        </div>
                        <div className="text-[11px] text-[#5E636E]">{u.role === "firm_admin" ? "Admin" : "Staff"}</div>
                    </Link>
                ))}
            </div>

            {stuck.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50/40 p-3" data-testid="dash-stuck">
                    <div className="flex items-center justify-between mb-1.5">
                        <div className="text-[12.5px] font-semibold text-red-800 flex items-center gap-1">
                            <Zap className="h-3.5 w-3.5" /> Stuck ({stuck.length})
                        </div>
                        <Link to="/app/job-flow" className="text-[11px] text-red-700 hover:underline">View all</Link>
                    </div>
                    <ul className="space-y-1">
                        {stuckClip.map((j) => (
                            <li key={j.id} className="text-[12px] flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                                <Link to={`/app/job-flow/${j.id}`} className="flex-1 truncate hover:underline text-[#0F1115]">
                                    {clientsById[j.client_id]?.name || (j.type === "others" ? "Internal" : "—")}
                                    <span className="text-[#5E636E]"> · {JOB_TYPE_LABEL[j.type]}</span>
                                </Link>
                                <span className="text-[10.5px] text-red-800 font-semibold truncate max-w-[180px]">
                                    {j.stuck_reasons.join(" · ")}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {jobs.length === 0 && (
                <div className="py-6 text-center text-[12.5px] text-[#5E636E]">
                    No open jobs. <Link to="/app/job-flow" className="underline">Create one</Link>.
                </div>
            )}
        </div>
    );
}

function MyQueue({ jobs, clientsById }) {
    if (!jobs.length) {
        return (
            <div className="py-8 text-center text-[12.5px] text-[#5E636E]">
                No open jobs assigned to you.
            </div>
        );
    }
    return (
        <ul className="mt-4 divide-y divide-[#E4E4E7]" data-testid="dash-my-jobs">
            {jobs.slice(0, 10).map((j) => {
                const overdue = j.due_date && new Date(j.due_date) < new Date();
                return (
                    <li key={j.id} className="py-2 flex items-center gap-2">
                        <ActivityIcon className="h-3.5 w-3.5 text-[#5E636E]" />
                        <Link to={`/app/job-flow/${j.id}`} className="flex-1 min-w-0 truncate text-[13px] hover:underline">
                            {clientsById[j.client_id]?.name || (j.type === "others" ? (j.title || "Internal") : "—")}
                            <span className="text-[#5E636E]"> · {JOB_TYPE_LABEL[j.type]}</span>
                        </Link>
                        {j.needs_michael && <Flag className="h-3 w-3 text-red-700" />}
                        {j.due_date && (
                            <span className={`text-[11px] ${overdue ? "text-red-700 font-semibold" : "text-[#5E636E]"}`}>
                                <Clock className="h-3 w-3 inline" /> {j.due_date}
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
